import React, { useState } from 'react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

/**
 * 2026-02 FIX 2 — Reusable archive controls.
 *
 *   <ArchiveButton kind="property" id={p.id} label="Edit details" onArchived={...} />
 *   <ArchivedBadge />
 *   <RestoreButton kind="property" id={p.id} onRestored={...} />
 *
 * Archive flow always shows the canonical confirmation prompt the user
 * specified:
 *   "Are you sure you want to archive this record? It will be hidden
 *    from your main views and permanently deleted after 30 days."
 *
 * NOTE: case (Section 8) records carry `is_deletable=false` server-side,
 * so the 30-day wording is suppressed for them via the `legalRetention`
 * prop on this component.
 */
export function ArchiveButton({
  kind,
  id,
  onArchived,
  size = 'sm',
  variant = 'subtle',
  legalRetention = false,
  testid,
  label = 'Archive',
}) {
  const [busy, setBusy] = useState(false);
  const handleClick = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    const msg = legalRetention
      ? 'Are you sure you want to archive this case? It will be hidden from your main views. Legal audit records are NEVER permanently deleted — they remain in the archive indefinitely.'
      : 'Are you sure you want to archive this record? It will be hidden from your main views and permanently deleted after 30 days.';
    // eslint-disable-next-line no-alert
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/archive/${kind}/${id}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!res.ok) throw new Error('archive failed');
      onArchived && onArchived(id);
    } catch (err) {
      // eslint-disable-next-line no-alert
      window.alert('Could not archive. Please try again.');
    } finally {
      setBusy(false);
    }
  };
  const pad = size === 'xs' ? '0.25rem 0.55rem' : '0.4rem 0.85rem';
  const fontSize = size === 'xs' ? '0.72rem' : '0.78rem';
  const base = {
    border: 'none', borderRadius: 6,
    padding: pad, fontWeight: 600, fontSize, cursor: busy ? 'wait' : 'pointer',
  };
  const palette = variant === 'danger'
    ? { background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca' }
    : { background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0' };
  return (
    <button type="button"
            onClick={handleClick}
            disabled={busy}
            data-testid={testid || `archive-${kind}-${id}`}
            style={{ ...base, ...palette }}>
      {busy ? 'Archiving…' : label}
    </button>
  );
}

export function RestoreButton({ kind, id, onRestored, testid }) {
  const [busy, setBusy] = useState(false);
  const handleClick = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/archive/${kind}/${id}/restore`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) throw new Error('restore failed');
      onRestored && onRestored(id);
    } catch {
      // eslint-disable-next-line no-alert
      window.alert('Could not restore. Please try again.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <button type="button"
            onClick={handleClick}
            disabled={busy}
            data-testid={testid || `restore-${kind}-${id}`}
            style={{ background: '#ecfeff', color: '#0369a1',
                     border: '1px solid #bae6fd', borderRadius: 6,
                     padding: '0.3rem 0.7rem', fontWeight: 600,
                     fontSize: '0.75rem', cursor: busy ? 'wait' : 'pointer' }}>
      {busy ? 'Restoring…' : 'Restore'}
    </button>
  );
}

export function ArchivedBadge({ small = false }) {
  return (
    <span data-testid="archived-badge"
          style={{ background: '#e5e7eb', color: '#475569',
                   fontSize: small ? '0.65rem' : '0.7rem',
                   padding: small ? '0.1rem 0.4rem' : '0.15rem 0.5rem',
                   borderRadius: 999, fontWeight: 600,
                   textTransform: 'uppercase', letterSpacing: '0.05em' }}>
      Archived
    </span>
  );
}

/**
 * Show-archived toggle — usage:
 *   const [showArchived, setShowArchived] = useState(false);
 *   <ShowArchivedToggle value={showArchived} onChange={setShowArchived} />
 *   const visible = items.filter(it => showArchived ? true : !it.archived);
 */
export function ShowArchivedToggle({ value, onChange, testid = 'toggle-show-archived' }) {
  return (
    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6,
                    fontSize: '0.85rem', color: '#475569', cursor: 'pointer',
                    userSelect: 'none' }}
           data-testid={testid}>
      <input type="checkbox"
             checked={!!value}
             onChange={(e) => onChange(e.target.checked)}
             data-testid={`${testid}-checkbox`}
             style={{ accentColor: '#0073EA' }} />
      Show archived
    </label>
  );
}
